import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  setSession: () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the /auth/me check if we're handling a Google OAuth callback —
    // AuthCallback will exchange the session_id and set our JWT first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("allure_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("allure_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const setSession = useCallback((token, userData) => {
    localStorage.setItem("allure_token", token);
    setUser(userData);
  }, []);

  const login = useCallback(async (email, password, rememberMe = true) => {
    // Persist email choice so the next visit pre-fills the field if the
    // photographer asked us to remember them.
    if (rememberMe) {
      localStorage.setItem("allure_remember_email", email);
      localStorage.setItem("allure_remember_me", "true");
    } else {
      localStorage.removeItem("allure_remember_email");
      localStorage.setItem("allure_remember_me", "false");
    }
    const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
    localStorage.setItem("allure_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget server-side revoke so the JWT can't be reused before
    // its natural expiry. We don't await it — the UI logs out immediately
    // regardless of network state. The /auth/logout endpoint is idempotent.
    api.post("/auth/logout").catch(() => { /* offline / already revoked — no-op */ });
    localStorage.removeItem("allure_token");
    setUser(null);
  }, []);

  // Memo so consumers don't re-render every time AuthProvider re-renders for any unrelated reason
  const value = useMemo(
    () => ({ user, loading, login, setSession, logout }),
    [user, loading, login, setSession, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
